import React, { useState, useEffect } from 'react';
import { Search, Bell, User, LogOut, Menu, X, Play, Info, ChevronRight, ChevronLeft, Plus, Star, Calendar, Clock, Globe, Dices, Bookmark } from 'lucide-react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Movie, UserProfile } from '../types';
import { generateSlug } from '../utils/slug';
import { searchTMDB, clearTMDBCache, getPosterSrcSet, getHeroSrcSet } from '../services/tmdb';
import { SUPPORTED_LANGUAGES, getTMDBLanguage, setTMDBLanguage, getTranslation } from '../utils/translations';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

// Utility for tailwind classes
export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// --- Components ---

export const Navbar: React.FC<{ user: UserProfile | null }> = ({ user }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<Movie[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [currentLang, setCurrentLang] = useState(getTMDBLanguage());
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = React.useRef<HTMLDivElement>(null);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  useEffect(() => {
    const handleLangChange = () => {
      setCurrentLang(getTMDBLanguage());
    };
    window.addEventListener('languageChanged', handleLangChange);
    return () => window.removeEventListener('languageChanged', handleLangChange);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(event.target as Node)) {
        setShowResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const timer = setTimeout(async () => {
      if (searchQuery.trim().length > 1) {
        setIsSearching(true);
        const results = await searchTMDB(searchQuery.trim());
        setSearchResults(results.slice(0, 6)); // Show top 6 results
        setIsSearching(false);
        setShowResults(true);
      } else {
        setSearchResults([]);
        setShowResults(false);
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setShowResults(false);
      navigate(`/search?q=${encodeURIComponent(searchQuery.trim())}`);
    } else {
      navigate('/');
    }
  };

  const [isLoggingIn, setIsLoggingIn] = useState(false);

  const handleLogin = async (e?: React.MouseEvent) => {
    if (e) {
      e.preventDefault();
      e.stopPropagation();
    }
    if (isLoggingIn) return;
    
    setIsLoggingIn(true);
    
    try {
      const [{ signInWithPopup, GoogleAuthProvider }, { doc, getDoc, setDoc }, { auth, db }] = await Promise.all([
        import('firebase/auth'),
        import('firebase/firestore'),
        import('../firebase'),
      ]);
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ prompt: 'select_account' });
      const result = await signInWithPopup(auth, provider);
      const user = result.user;
      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);
      
      if (!userSnap.exists()) {
        await setDoc(userRef, {
          uid: user.uid,
          email: user.email,
          displayName: user.displayName,
          photoURL: user.photoURL,
          role: 'user'
        });
      }
    } catch (error: any) {
      console.error('Login failed:', error);
      if (error.code === 'auth/unauthorized-domain') {
        alert(`Login failed: This domain is not authorized for Firebase Auth.\n\nPlease go to Firebase Console > Authentication > Settings > Authorized Domains and add:\n${window.location.hostname}`);
      } else if (error.code === 'auth/popup-closed-by-user' || error.code === 'auth/cancelled-popup-request' || error.code === 'auth/popup-blocked') {
        alert("Login popup was blocked or closed.\n\nIf you are viewing this in the AI Studio preview, please click the 'Open in new tab' button (square with arrow) in the top right corner of the screen, and try again.");
      } else if (error.message && error.message.includes('third-party cookies')) {
        alert("Login failed due to third-party cookie restrictions.\n\nPlease open the app in a new tab or enable third-party cookies in your browser settings.");
      } else {
        alert(`Login failed: ${error.message || 'Please try again. You may need to open the app in a new tab or allow popups.'}`);
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  const handleLogout = async () => {
    const [{ signOut }, { auth }] = await Promise.all([
      import('firebase/auth'),
      import('../firebase'),
    ]);
    await signOut(auth);
  };

  return (
    <nav className={cn(
      "fixed top-0 w-full z-50 transition-all duration-500 px-4 md:px-12 py-4 flex flex-col items-stretch",
      isScrolled ? "bg-black/70 backdrop-blur-xl border-b border-white/5 shadow-2xl" : "bg-gradient-to-b from-black/90 via-black/50 to-transparent"
    )}>
      <div className="flex items-center justify-between w-full">
        <div className="flex items-center gap-3 sm:gap-4 md:gap-8">
          <button
            type="button"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden text-white hover:text-red-500 transition-colors p-2 min-w-[40px] min-h-[40px] flex items-center justify-center focus:outline-none"
            aria-label="Toggle Navigation Menu"
            aria-expanded={isMobileMenuOpen}
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          <Link to="/" className="text-red-500 text-xl sm:text-2xl md:text-3xl font-black tracking-tighter hover:scale-105 transition-transform shrink-0 py-1" onClick={() => { setSearchQuery(''); setIsMobileMenuOpen(false); }}>CineStream</Link>
          <div className="hidden lg:flex items-center gap-6 text-sm font-bold text-gray-300">
            <Link to="/" className={cn("hover:text-white transition-colors py-1", location.pathname === '/' ? "text-white" : "")} onClick={() => setSearchQuery('')}>{getTranslation('home')}</Link>
            <Link to="/tv" className={cn("hover:text-white transition-colors py-1", location.pathname === '/tv' ? "text-white" : "")}>{getTranslation('tvShows')}</Link>
            <Link to="/movies" className={cn("hover:text-white transition-colors py-1", location.pathname === '/movies' ? "text-white" : "")}>{getTranslation('movies')}</Link>
            <Link to="/watchlist" className={cn("hover:text-white transition-colors flex items-center gap-1 py-1", location.pathname === '/watchlist' ? "text-white" : "")}>
              <Bookmark className="w-3.5 h-3.5 text-red-400 fill-current" />
              <span>Watchlist</span>
            </Link>
            {user && <Link to="/history" className={cn("hover:text-white transition-colors py-1", location.pathname === '/history' ? "text-white" : "")}>{getTranslation('continueWatching')}</Link>}
            <button
              type="button"
              onClick={() => window.dispatchEvent(new CustomEvent('openSurpriseMe'))}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-gradient-to-r from-red-600/20 to-amber-600/20 hover:from-red-600 hover:to-amber-600 border border-red-500/30 text-white text-xs font-bold transition-all shadow-md active:scale-95 cursor-pointer"
            >
              <Dices className="w-3.5 h-3.5 text-amber-400 fill-amber-400/20" />
              <span>Surprise Me</span>
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2.5 sm:gap-3 md:gap-5 text-white">
          <div ref={searchRef} className="relative">
            <form onSubmit={handleSearch}>
              <input
                type="search"
                aria-label="Search movies and tv shows"
                placeholder={getTranslation('searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={() => searchQuery.trim().length > 1 && setShowResults(true)}
                className="bg-black/60 border border-white/25 rounded-full py-2 pl-9 pr-3 text-sm min-h-[38px] text-white placeholder:text-gray-300 focus:outline-none focus:border-red-500 w-28 sm:w-40 lg:w-64 transition-all"
              />
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-300 pointer-events-none" />
            </form>

            {showResults && (searchResults.length > 0 || isSearching) && (
              <div className="absolute right-0 md:left-0 md:right-auto top-full mt-2 w-72 sm:w-80 lg:w-96 bg-zinc-900 border border-white/10 rounded-xl overflow-hidden shadow-2xl z-[70] origin-top-right md:origin-top-left">
                {isSearching ? (
                  <div className="p-4 text-center text-gray-300 text-sm animate-pulse">
                    Searching...
                  </div>
                ) : (
                  <div className="p-2 space-y-1">
                    {searchResults.map((movie) => (
                      <button
                        type="button"
                        key={movie.id}
                        onClick={() => {
                          navigate(`${generateSlug(movie)}?type=${movie.type}`);
                          setShowResults(false);
                          setSearchQuery('');
                          setIsMobileMenuOpen(false);
                        }}
                        className="w-full flex items-center gap-3 p-2 hover:bg-white/5 rounded-lg transition-colors text-left group"
                      >
                        <div className="w-12 h-16 shrink-0 rounded overflow-hidden bg-zinc-800">
                          <img 
                            src={movie.thumbnail || `https://placehold.co/400x600/000000/e11d48?text=${encodeURIComponent(movie.title)}`} 
                            srcSet={getPosterSrcSet(movie.thumbnail)}
                            sizes="48px"
                            alt={movie.title}
                            width={48}
                            height={64}
                            loading="lazy"
                            decoding="async"
                            className="w-full h-full object-cover"
                            referrerPolicy="no-referrer"
                            onError={(e) => {
                              (e.target as HTMLImageElement).src = `https://placehold.co/400x600/000000/e11d48?text=${encodeURIComponent(movie.title)}`;
                            }}
                          />
                        </div>
                        <div className="flex-1 min-w-0">
                          <h4 className="text-sm font-bold text-white truncate group-hover:text-red-400 transition-colors">
                            {movie.title}
                          </h4>
                          <div className="flex items-center gap-2 mt-1 text-[10px] text-gray-300 font-bold">
                            <span className="px-1.5 py-0.5 bg-zinc-800 rounded text-white uppercase">{movie.type}</span>
                            {movie.year && (
                              <div className="flex items-center gap-1">
                                <Calendar className="w-3 h-3" />
                                <span>{movie.year}</span>
                              </div>
                            )}
                            {movie.rating && (
                              <div className="flex items-center gap-1 text-yellow-400">
                                <Star className="w-3 h-3 fill-current" />
                                <span>{movie.rating}</span>
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={handleSearch}
                      className="w-full py-2.5 text-xs font-bold text-red-400 hover:bg-red-600/10 rounded-lg transition-colors mt-1"
                    >
                      View all results for "{searchQuery}"
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
          
          {/* Language Selector */}
          <div className="relative flex items-center bg-zinc-900 border border-white/15 rounded-full px-3 py-1.5 min-h-[38px] text-xs font-bold gap-1.5 hover:border-white/30 transition-all select-none">
            <Globe className="w-3.5 h-3.5 text-gray-300 shrink-0 pointer-events-none" />
            <select
              value={currentLang}
              onChange={(e) => {
                setTMDBLanguage(e.target.value);
                clearTMDBCache();
                window.location.reload();
              }}
              className="bg-transparent text-white outline-none cursor-pointer pr-1 font-bold text-xs appearance-none py-1"
              style={{ WebkitAppearance: 'none', MozAppearance: 'none' }}
              aria-label="Select Language"
            >
              {SUPPORTED_LANGUAGES.map(l => (
                <option key={l.code} value={l.code} className="bg-zinc-950 text-white font-bold text-xs">
                  {l.flag} {l.code.split('-')[0].toUpperCase()}
                </option>
              ))}
            </select>
          </div>

          <button
            type="button"
            aria-label="Notifications"
            className="p-2 hover:text-red-400 transition-colors hidden sm:flex items-center justify-center min-w-[38px] min-h-[38px]"
          >
            <Bell className="w-5 h-5" />
          </button>
          
          {user ? (
            <div className="group relative">
              <img 
                src={user.photoURL || "https://picsum.photos/seed/user/100/100"} 
                alt="Profile" 
                width={32}
                height={32}
                loading="lazy"
                className="w-8 h-8 rounded cursor-pointer border border-white/20"
              />
              <div className="absolute right-0 top-full mt-2 w-48 bg-zinc-950 border border-white/10 rounded-md py-2 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all shadow-2xl z-[60]">
                <div className="px-4 py-2 border-b border-white/10 text-xs text-gray-300 truncate">
                  {user.email}
                </div>
                <button 
                  type="button"
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-2.5 text-sm hover:bg-white/10 flex items-center gap-2 text-white"
                >
                  <LogOut className="w-4 h-4" /> Sign Out
                </button>
              </div>
            </div>
          ) : (
            <button 
              type="button"
              onClick={() => handleLogin()}
              disabled={isLoggingIn}
              className="bg-red-600 text-white px-4 sm:px-6 py-2 min-h-[38px] rounded-md text-sm font-bold hover:bg-red-700 transition-all active:scale-95 shadow-lg shadow-red-600/20 z-[60] disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
            >
              {isLoggingIn ? 'Signing In...' : 'Sign In'}
            </button>
          )}
        </div>
      </div>

      {/* Mobile Menu Drawer */}
      {isMobileMenuOpen && (
        <div className="w-full bg-transparent overflow-hidden flex flex-col lg:hidden space-y-4 pt-4">
          <div className="flex flex-col gap-1 text-sm font-bold text-gray-400 bg-zinc-950/95 backdrop-blur-md rounded-2xl border border-white/10 p-4 shadow-2xl">
            <Link
              to="/"
              className={cn("hover:text-white transition-colors py-2.5 px-3 rounded-lg flex items-center justify-between", location.pathname === '/' ? "text-white bg-white/5" : "hover:bg-white/5")}
              onClick={() => {
                setSearchQuery('');
                setIsMobileMenuOpen(false);
              }}
            >
              <span>{getTranslation('home')}</span>
              {location.pathname === '/' && <span className="w-1.5 h-1.5 rounded-full bg-red-600" />}
            </Link>
            <Link
              to="/tv"
              className={cn("hover:text-white transition-colors py-2.5 px-3 rounded-lg flex items-center justify-between", location.pathname === '/tv' ? "text-white bg-white/5" : "hover:bg-white/5")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span>{getTranslation('tvShows')}</span>
              {location.pathname === '/tv' && <span className="w-1.5 h-1.5 rounded-full bg-red-600" />}
            </Link>
            <Link
              to="/movies"
              className={cn("hover:text-white transition-colors py-2.5 px-3 rounded-lg flex items-center justify-between", location.pathname === '/movies' ? "text-white bg-white/5" : "hover:bg-white/5")}
              onClick={() => setIsMobileMenuOpen(false)}
            >
              <span>{getTranslation('movies')}</span>
              {location.pathname === '/movies' && <span className="w-1.5 h-1.5 rounded-full bg-red-600" />}
            </Link>
            {user && (
              <Link
                to="/history"
                className={cn("hover:text-white transition-colors py-2.5 px-3 rounded-lg flex items-center justify-between", location.pathname === '/history' ? "text-white bg-white/5" : "hover:bg-white/5")}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                <span>{getTranslation('continueWatching')}</span>
                {location.pathname === '/history' && <span className="w-1.5 h-1.5 rounded-full bg-red-600" />}
              </Link>
            )}

            {/* Mobile Actions in the drawer */}
            <div className="pt-3 mt-2 border-t border-white/5">
              {user ? (
                <div className="p-3 bg-white/5 rounded-xl border border-white/5 flex items-center justify-between">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <img
                      src={user.photoURL || "https://picsum.photos/seed/user/100/100"}
                      alt="Profile"
                      className="w-8 h-8 rounded-full border border-white/10 object-cover shrink-0"
                    />
                    <div className="min-w-0">
                      <p className="text-xs font-bold text-white truncate">{user.displayName || 'User'}</p>
                      <p className="text-[10px] text-gray-500 truncate">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => {
                      handleLogout();
                      setIsMobileMenuOpen(false);
                    }}
                    className="p-1.5 bg-white/5 hover:bg-red-600/10 hover:text-red-500 rounded-lg text-gray-400 transition-all shrink-0"
                    aria-label="Sign Out"
                  >
                    <LogOut className="w-3.5 h-3.5" />
                  </button>
                </div>
              ) : (
                <button
                  onClick={() => {
                    handleLogin();
                    setIsMobileMenuOpen(false);
                  }}
                  disabled={isLoggingIn}
                  className="w-full bg-red-600 hover:bg-red-700 text-white py-2.5 rounded-xl text-xs font-bold transition-all active:scale-95 shadow-lg shadow-red-600/20 disabled:opacity-50"
                >
                  {isLoggingIn ? 'Signing In...' : 'Sign In with Google'}
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export const Hero: React.FC<{ movies: Movie[] }> = ({ movies }) => {
  const navigate = useNavigate();
  const [currentIndex, setCurrentIndex] = useState(0);

  useEffect(() => {
    if (!movies || movies.length <= 1) return;
    const interval = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % movies.length);
    }, 6000);
    return () => clearInterval(interval);
  }, [movies]);

  if (!movies || movies.length === 0) {
    return (
      <div className="relative h-[75vh] md:h-[85vh] w-full overflow-hidden bg-zinc-950 flex items-end pb-12 px-4 md:px-12">
        <div className="max-w-2xl w-full space-y-4 animate-pulse">
          <div className="h-10 sm:h-14 w-2/3 bg-zinc-800/80 rounded-xl" />
          <div className="h-4 w-full bg-zinc-800/60 rounded-lg" />
          <div className="h-4 w-4/5 bg-zinc-800/60 rounded-lg" />
          <div className="flex gap-4 pt-2">
            <div className="h-12 w-36 bg-red-600/80 rounded-xl" />
            <div className="h-12 w-36 bg-zinc-800/80 rounded-xl" />
          </div>
        </div>
      </div>
    );
  }

  const movie = movies[currentIndex] || movies[0];
  const heroImgUrl = movie.backdrop || movie.thumbnail;

  const handlePrev = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev - 1 + movies.length) % movies.length);
  };

  const handleNext = (e: React.MouseEvent) => {
    e.stopPropagation();
    setCurrentIndex(prev => (prev + 1) % movies.length);
  };

  return (
    <div className="relative h-[75vh] md:h-[85vh] w-full overflow-hidden group/hero bg-zinc-950">
      <div
        key={movie.id}
        className="absolute inset-0 w-full h-full transition-opacity duration-500"
      >
        <img 
          src={heroImgUrl || `https://placehold.co/1280x720/000000/e11d48?text=${encodeURIComponent(movie.title)}`} 
          srcSet={getHeroSrcSet(heroImgUrl)}
          sizes="(max-width: 640px) 480px, (max-width: 1024px) 780px, 1280px"
          alt={movie.title} 
          width={1280}
          height={720}
          fetchPriority="high"
          decoding="async"
          className="absolute inset-0 w-full h-full object-cover"
          referrerPolicy="no-referrer"
          onError={(e) => {
            const target = e.target as HTMLImageElement;
            target.removeAttribute('srcset');
            const imdbId = movie.imdbId;
            
            // Progressive fallback chain for Hero (prefers backdrops)
            if (imdbId && !target.src.includes('backdrop')) {
              target.src = `https://www.2embed.cc/images/backdrop/imdb/${imdbId}.jpg`;
            } else if (imdbId && !target.src.includes('2embed.cc')) {
              target.src = `https://www.2embed.cc/images/poster/imdb/${imdbId}.jpg`;
            } else if (imdbId && !target.src.includes('vidsrc.me')) {
              target.src = `https://vidsrc.me/poster/${imdbId}`;
            } else {
              target.src = `https://placehold.co/1280x720/000000/e11d48?text=${encodeURIComponent(movie.title)}`;
            }
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-black via-black/40 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/20 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-tr from-red-900/30 via-transparent to-transparent mix-blend-overlay hidden md:block" />
        
        <div className="absolute bottom-[10%] md:bottom-[15%] left-4 right-4 md:left-12 md:right-12 max-w-4xl space-y-4 md:space-y-6 z-20">
          <div className="flex flex-col md:flex-row md:items-end gap-3 md:gap-6">
            <h2 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black text-transparent bg-clip-text bg-gradient-to-br from-white via-white to-gray-300 drop-shadow-2xl leading-none tracking-tighter uppercase break-words">{movie.title}</h2>
            {movie.quality && (
              <span className="px-3 py-1.5 bg-red-600 text-white text-[10px] md:text-sm font-black rounded uppercase tracking-widest w-fit mb-2 md:mb-3 shadow-[0_0_20px_rgba(220,38,38,0.5)]">
                {movie.quality}
              </span>
            )}
          </div>
          <p className="text-sm md:text-lg lg:text-xl text-gray-200 line-clamp-3 drop-shadow-lg max-w-2xl font-medium leading-relaxed">{movie.description}</p>
          
          <div className="flex flex-wrap items-center gap-4 pt-4">
            <button 
              type="button"
              onClick={() => navigate(`${generateSlug(movie)}?type=${movie.type}`)}
              className="group/btn relative flex items-center gap-3 bg-red-600 text-white px-8 md:px-12 py-3 md:py-4 rounded-xl font-black hover:bg-red-700 transition-all active:scale-95 shadow-[0_0_40px_rgba(220,38,38,0.5)] hover:shadow-[0_0_60px_rgba(220,38,38,0.7)] text-sm md:text-lg cursor-pointer overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent translate-x-[-100%] group-hover/btn:translate-x-[100%] transition-transform duration-700" />
              <Play className="w-5 h-5 md:w-6 md:h-6 fill-current text-white relative z-10" /> <span className="relative z-10">Watch Now</span>
            </button>
            <button 
              type="button"
              onClick={() => navigate(`${generateSlug(movie)}?type=${movie.type}`)}
              className="flex items-center gap-3 bg-white/10 text-white px-8 md:px-12 py-3 md:py-4 rounded-xl font-black hover:bg-white/15 transition-all backdrop-blur-md border border-white/15 shadow-xl text-sm md:text-lg cursor-pointer hover:border-white/30"
            >
              <Info className="w-5 h-5 md:w-6 md:h-6" /> More Info
            </button>
          </div>
        </div>
      </div>

      {/* Manual Slide Navigation */}
      <button 
        type="button"
        onClick={handlePrev}
        aria-label="Previous slide"
        className="absolute left-4 top-1/2 -translate-y-1/2 z-30 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full bg-black/50 hover:bg-red-600 border border-white/15 text-white opacity-0 group-hover/hero:opacity-100 transition-all active:scale-95 duration-300 backdrop-blur-sm cursor-pointer"
      >
        <ChevronLeft className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      <button 
        type="button"
        onClick={handleNext}
        aria-label="Next slide"
        className="absolute right-4 top-1/2 -translate-y-1/2 z-30 p-3 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full bg-black/50 hover:bg-red-600 border border-white/15 text-white opacity-0 group-hover/hero:opacity-100 transition-all active:scale-95 duration-300 backdrop-blur-sm cursor-pointer"
      >
        <ChevronRight className="w-5 h-5 md:w-6 md:h-6" />
      </button>

      {/* Indicators/Dots with accessible names and >=32px touch targets */}
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-30 flex items-center gap-1" role="tablist" aria-label="Featured slides">
        {movies.slice(0, 10).map((m, idx) => (
          <button
            type="button"
            key={m.id || idx}
            role="tab"
            aria-selected={idx === currentIndex}
            aria-label={`Go to slide ${idx + 1}: ${m.title}`}
            onClick={() => setCurrentIndex(idx)}
            className="w-8 h-8 flex items-center justify-center rounded-full cursor-pointer focus:outline-none"
          >
            <span
              className={`block h-2 rounded-full transition-all duration-300 ${
                idx === currentIndex ? 'w-6 bg-red-600 shadow-lg shadow-red-600/50' : 'w-2 bg-white/50 hover:bg-white/80'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

export const MovieRow: React.FC<{ title: string; movies: Movie[]; link?: string; priority?: boolean }> = ({ title, movies, link, priority = false }) => {
  const rowRef = React.useRef<HTMLDivElement>(null);
  const sectionRef = React.useRef<HTMLDivElement>(null);
  const [isInView, setIsInView] = useState(priority);
  const [visibleCount, setVisibleCount] = useState(() =>
    typeof window !== 'undefined' && window.innerWidth < 768 ? 4 : 8
  );

  useEffect(() => {
    if (priority || isInView) return;
    const el = sectionRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') {
      setIsInView(true);
      return;
    }
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) {
          setIsInView(true);
          observer.disconnect();
        }
      },
      { rootMargin: '300px 0px' }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [priority, isInView]);

  const expandRow = () => {
    if (visibleCount < movies.length) {
      setVisibleCount(movies.length);
    }
  };

  const scroll = (direction: 'left' | 'right') => {
    expandRow();
    if (rowRef.current) {
      const { scrollLeft, clientWidth } = rowRef.current;
      const scrollTo = direction === 'left' ? scrollLeft - clientWidth : scrollLeft + clientWidth;
      rowRef.current.scrollTo({ left: scrollTo, behavior: 'smooth' });
    }
  };

  const renderedMovies = movies.slice(0, visibleCount);

  return (
    <div
      ref={sectionRef}
      onMouseEnter={expandRow}
      onTouchStart={expandRow}
      className="space-y-6 px-4 md:px-12 py-8 md:py-14 group/row"
      style={{ contentVisibility: 'auto', containIntrinsicSize: '1000px 300px' }}
    >
      <div className="flex items-center justify-between">
        {link ? (
          <Link to={link} className="group/title flex items-center gap-2 py-1">
            <h2 className="text-2xl font-bold text-white tracking-tight group-hover/title:text-red-500 transition-colors">{title}</h2>
            <ChevronRight className="w-6 h-6 text-red-500 opacity-0 -translate-x-2 group-hover/title:opacity-100 group-hover/title:translate-x-0 transition-all" />
          </Link>
        ) : (
          <h2 className="text-2xl font-bold text-white tracking-tight">{title}</h2>
        )}
      </div>
      <div className="relative">
        <button 
          type="button"
          onClick={() => scroll('left')}
          aria-label={`Scroll ${title} left`}
          className="absolute left-0 top-0 bottom-0 z-30 bg-black/60 opacity-0 group-hover/row:opacity-100 transition-opacity px-4 hover:bg-black/80"
        >
          <ChevronLeft className="text-white w-10 h-10" />
        </button>
        
        <div 
          ref={rowRef}
          onScroll={expandRow}
          className="flex gap-4 overflow-x-auto scrollbar-hide scroll-smooth pb-6 pt-2"
        >
          {isInView && renderedMovies.length > 0 ? (
            renderedMovies.map((movie) => (
              <Link 
                key={movie.id}
                to={`${generateSlug(movie)}?type=${movie.type}${movie.season ? `&s=${movie.season}` : ''}${movie.episode ? `&e=${movie.episode}` : ''}`}
                className="relative flex-none w-32 md:w-48 aspect-[2/3] rounded-lg overflow-hidden cursor-pointer transition-all duration-500 hover:scale-[1.08] hover:z-40 shadow-lg hover:shadow-[0_0_30px_rgba(220,38,38,0.4)] group/item border border-transparent hover:border-red-600/30 bg-zinc-900"
              >
                <img 
                  src={movie.thumbnail || `https://placehold.co/342x513/000000/e11d48?text=${encodeURIComponent(movie.title)}`} 
                  srcSet={getPosterSrcSet(movie.thumbnail)}
                  sizes="(max-width: 640px) 128px, 192px"
                  alt={movie.title} 
                  width={342}
                  height={513}
                  loading="lazy"
                  decoding="async"
                  className="w-full h-full object-cover"
                  referrerPolicy="no-referrer"
                  onError={(e) => {
                    const target = e.target as HTMLImageElement;
                    target.removeAttribute('srcset');
                    const imdbId = movie.imdbId;

                    if (imdbId && !target.src.includes('2embed.cc')) {
                      target.src = `https://www.2embed.cc/images/poster/imdb/${imdbId}.jpg`;
                    } else if (imdbId && !target.src.includes('vidsrc.me')) {
                      target.src = `https://vidsrc.me/poster/${imdbId}`;
                    } else if (imdbId && !target.src.includes('vidsrc.xyz')) {
                      target.src = `https://vidsrc.xyz/images/poster/imdb/${imdbId}.jpg`;
                    } else if (imdbId && !target.src.includes('img.vidsrc.me')) {
                      target.src = `https://img.vidsrc.me/poster/${imdbId}`;
                    } else {
                      target.src = `https://placehold.co/342x513/000000/e11d48?text=${encodeURIComponent(movie.title)}`;
                    }
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover/item:opacity-100 transition-opacity duration-300" />
                
                {movie.quality && (
                  <div className="absolute top-2 right-2 px-1.5 py-0.5 bg-red-600 text-white text-[8px] md:text-[10px] font-black rounded uppercase shadow-md z-10">
                    {movie.quality}
                  </div>
                )}

                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-4 group-hover/item:translate-y-0 opacity-0 group-hover/item:opacity-100 transition-all duration-300">
                  <p className="text-white text-xs md:text-sm font-black truncate mb-1 drop-shadow-md">{movie.title}</p>
                  <div className="flex items-center gap-2">
                     <div className="w-5 h-5 md:w-7 md:h-7 rounded-full bg-red-600 flex items-center justify-center shadow-lg">
                       <Play className="w-2.5 h-2.5 md:w-3.5 md:h-3.5 text-white fill-current" />
                     </div>
                     <span className="text-[10px] md:text-xs text-gray-200 font-bold drop-shadow-md">{movie.duration}</span>
                  </div>
                </div>

                {movie.progress !== undefined && movie.progress > 0 && (
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20 z-20">
                    <div 
                      className="h-full bg-red-600 transition-all duration-300" 
                      style={{ width: `${movie.progress}%` }} 
                    />
                  </div>
                )}
              </Link>
            ))
          ) : (
            Array.from({ length: 6 }).map((_, idx) => (
              <div key={idx} className="flex-none w-32 md:w-48 aspect-[2/3] rounded-lg bg-zinc-900/60 border border-white/5" />
            ))
          )}
        </div>
 
        <button 
          type="button"
          onClick={() => scroll('right')}
          aria-label={`Scroll ${title} right`}
          className="absolute right-0 top-0 bottom-0 z-30 bg-black/60 opacity-0 group-hover/row:opacity-100 transition-opacity px-4 hover:bg-black/80"
        >
          <ChevronRight className="text-white w-10 h-10" />
        </button>
      </div>
    </div>
  );
};
